import React, {
  useEffect,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import {
  Button,
  Form,
  FormFeedback,
  FormGroup,
  Input,
  Label,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from 'reactstrap';
import { useIntl } from 'react-intl';

import {
  fetchFeaturedCollections,
  refreshCollections,
} from '../../actions/Collections';

import {
  APIduplicateCollection,
} from '../../api/Collections';

import ErrorBoundary from '../ErrorBoundary';

import { isArchive } from '../../helpers/App';
import { userIsAdmin } from '../../helpers/Auth';
import Console from '../../helpers/Console';
import {
  convertParentUuidToNamedUuid,
  getCollectionUrl,
} from '../../helpers/Collections';
import { eventDispatch } from '../../helpers/Events';
import { addFlashMessage } from '../../helpers/FlashMessages';
import { getSetting } from '../../helpers/Settings';

import CollectionSelectCollectionMessage from './SelectCollectionMessage';
import CollectionsTree from './Tree';
import ProcessingSpinner from '../ProcessingSpinner';

const CollectionDuplicateModal = ({
  collection,
  parentCollection,
  reloadCollectionList,
  loadFeaturedCollections,
  afterDuplicateCollection,
  handleClose,
}) => {
  const intl = useIntl();

  const [parentCollectionUuid, setParentCollectionUuid] = useState(null);
  const [collectionName, setCollectionName] = useState('');
  const [includeFiles, setIncludeFiles] = useState(true);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setCollectionName(collection?.name);
  }, [collection]);

  const handleSaveCollection = async () => {
    setLoading(true);

    try {
      const response = await APIduplicateCollection({
        parentCollectionUuid,
        collectionName,
        collectionUuidList: [collection.uuid],
        includeFiles,
      });

      if (response.status === 200) {
        response.data?.data.forEach((respCollection) => {
          if (respCollection && respCollection.uuid) {
            if (respCollection.featured) {
              loadFeaturedCollections();
            }
            eventDispatch('collectionUpdated', respCollection);
            eventDispatch('collectionUpdatedTree', respCollection);
            if (
              convertParentUuidToNamedUuid(parentCollection?.uuid) ===
              convertParentUuidToNamedUuid(parentCollectionUuid)) {
              reloadCollectionList();
            }
            addFlashMessage({
              type: 'success',
              text: intl.formatMessage({ defaultMessage: 'Collections have been duplicated successfully.' }),
              button: {
                url: getCollectionUrl(parentCollectionUuid),
                text: intl.formatMessage({ defaultMessage: 'Open collection' }),
              },
            });
            afterDuplicateCollection(respCollection);
          }
        });

        response.data?.errors?.forEach((error) => {
          addFlashMessage({
            type: 'warning',
            text: error,
          });
        });
      }
    } catch (error) {
      Console.log(error);
    } finally {
      setLoading(false);
      handleClose();
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSaveCollection();
    }
  };

  const validateCollectionName = (newName) => {
    if (!newName.trim()) {
      return {
        collectionName: intl.formatMessage({
          defaultMessage: 'Collection name cannot be empty.',
        }),
      };
    }
    if (newName.length > 191) {
      return {
        collectionName: intl.formatMessage({
          defaultMessage: 'Collection name cannot exceed 191 characters.',
        }),
      };
    }
    return {};
  };

  const handleChangeCollectionName = (e) => {
    const newName = e.target.value;
    setCollectionName(newName);

    const newErrors = validateCollectionName(newName);
    if (Object.keys(newErrors).length > 0) {
      setErrors({ ...errors, ...newErrors });
    } else {
      const updatedErrors = { ...errors };
      delete updatedErrors.collectionName;
      setErrors(updatedErrors);
    }
  };

  const isValidForm = () => Object.keys(errors).length === 0;

  return (
    <ErrorBoundary>
      <ModalHeader toggle={handleClose} className="modal-header-center">
        {intl.formatMessage({ defaultMessage: 'Duplicate Collection' })}
      </ModalHeader>
      <ModalBody>
        <Form
          className="duplicate-collection-form"
          onKeyDown={handleKeyDown}
        >
          <FormGroup>
            <Label for="inpCollectionName">
              {intl.formatMessage({ defaultMessage: 'Collection name' })}
            </Label>
            <Input
              type="text"
              name="collectionName"
              id="inpCollectionName"
              placeholder={intl.formatMessage({ defaultMessage: 'Collection name' })}
              autoFocus={true}
              value={collectionName}
              onChange={handleChangeCollectionName}
              invalid={!!errors.collectionName}
            />
            <FormFeedback>
              {errors.collectionName}
            </FormFeedback>
          </FormGroup>
          <FormGroup>
            <Label for="includeFiles" className="switch switch-left">
              <div className="switch-control input-inner">
                <Input
                  type="checkbox"
                  name="includeFiles"
                  id="includeFiles"
                  value="1"
                  defaultChecked={includeFiles}
                  disabled={loading}
                  onClick={() => { setIncludeFiles(!includeFiles); }}
                />
                <i />
              </div>
              <div className="switch-details">
                <span>
                  {intl.formatMessage({ defaultMessage: 'Include files' })}
                </span>
              </div>
            </Label>
          </FormGroup>
          <FormGroup>
            <CollectionSelectCollectionMessage />
            <CollectionsTree
              selectedCollections={[convertParentUuidToNamedUuid(parentCollection?.uuid)]}
              onlyMyCollections={getSetting('cs1') && !userIsAdmin()}
              onlyOpenCollections={true}
              enableAllCollections={true}
              addNewDisabled={loading}
              onChange={(selectedUuids) => {
                if (selectedUuids.length > 0) {
                  setParentCollectionUuid(selectedUuids[0]);
                } else {
                  setParentCollectionUuid(null);
                }
              }}
            />
          </FormGroup>
        </Form>
      </ModalBody>
      <ModalFooter>
        <div className="buttons-group">
          <Button
            color="secondary"
            disabled={loading}
            onClick={handleClose}
          >
            {intl.formatMessage({ defaultMessage: 'Cancel' })}
          </Button>
          <Button
            color="primary"
            disabled={loading || !parentCollectionUuid || !isValidForm()}
            onClick={handleSaveCollection}
          >
            <ProcessingSpinner show={loading} />
            {intl.formatMessage({ defaultMessage: 'Duplicate' })}
          </Button>
        </div>
      </ModalFooter>
    </ErrorBoundary>
  );
};

const mapStateToProps = () => ({
});

const mapDispatchToProps = dispatch => ({
  loadFeaturedCollections: () => {
    if (isArchive()) {
      dispatch(fetchFeaturedCollections({ archived: true }));
    } else {
      dispatch(fetchFeaturedCollections());
    }
  },
  reloadCollectionList: () => {
    dispatch(refreshCollections());
  },
});

CollectionDuplicateModal.propTypes = {
  collection: PropTypes.object.isRequired,
  parentCollection: PropTypes.object,
  reloadCollectionList: PropTypes.func.isRequired,
  loadFeaturedCollections: PropTypes.func.isRequired,
  afterDuplicateCollection: PropTypes.func,
  handleClose: PropTypes.func.isRequired,
};

CollectionDuplicateModal.defaultProps = {
  parentCollection: null,
  afterDuplicateCollection: () => {},
};

export default connect(mapStateToProps, mapDispatchToProps)(CollectionDuplicateModal);
